import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AppCatalogEntry, AppRunDetail, AppRunExecution } from '@api/apps';
import type { ConversationAppRun } from '@contexts/ConversationsContextStore';
import { appRunPath } from '../appRunPath';
import { useAppCatalog, useAppRunActions } from '../useApps';
import type { AutostartRequest, RunHost } from './runHost';

/** Options carried across the navigation that turns a draft into a real run. */
interface AutostartState {
  autostart?: Record<string, unknown>;
  prompt?: string;
  /** Where the person chose to run it; absent for an app that only runs in the cloud. */
  execution?: AppRunExecution | null;
}

export interface RouteRunHost extends RunHost {
  readonly catalogEntry: AppCatalogEntry | null;
}

const appRunOf = (run: AppRunDetail): ConversationAppRun => ({
  runId: run.runId,
  appKey: run.appKey,
  nameKey: run.manifest.name.id,
  nameDefault: run.manifest.name.default,
});

export const useRouteRunHost = (appKey: string | undefined, runId: string | undefined): RouteRunHost => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { data: catalog, isLoading } = useAppCatalog();
  const { create } = useAppRunActions();
  const catalogEntry = catalog?.find((entry) => entry.appKey === appKey) ?? null;

  const routeState = state as AutostartState | null;
  const autostartFor = useCallback(
    (id: string): AutostartRequest | null => {
      if (id !== runId || !routeState?.autostart) return null;
      return { options: routeState.autostart, prompt: routeState.prompt, execution: routeState.execution };
    },
    [routeState, runId],
  );

  const openRun = useCallback(() => create(appKey as string), [appKey, create]);

  const showRun = useCallback(
    (id: string, request: AutostartRequest) => {
      const autostart: AutostartState = {
        autostart: request.options,
        prompt: request.prompt,
        execution: request.execution ?? null,
      };
      navigate(appRunPath({ appKey: appKey as string, runId: id }), { replace: !runId, state: autostart });
    },
    [appKey, navigate, runId],
  );

  const startOver = useCallback(() => {
    if (appKey) navigate(`/apps/${encodeURIComponent(appKey)}`);
  }, [appKey, navigate]);

  const openTranscript = useCallback(
    (run: AppRunDetail) => navigate(`/conversation/${run.conversationId}`, { state: { appRun: appRunOf(run) } }),
    [navigate],
  );

  return useMemo<RouteRunHost>(
    () => ({
      appKey,
      runId,
      composeManifest: catalogEntry?.manifest ?? null,
      composeLoading: isLoading,
      composeCrossOrg: Boolean(catalogEntry?.crossOrg),
      testRuns: false,
      setupTarget: 'catalog',
      autostartFor,
      openRun,
      showRun,
      startOver,
      openTranscript,
      catalogEntry,
    }),
    [appKey, autostartFor, catalogEntry, isLoading, openRun, openTranscript, runId, showRun, startOver],
  );
};
